import { useRef, useEffect, useState, useContext } from "react";
import { Modal, Badge, Placeholder } from "react-bootstrap";
import {
  getPlayerShortTeam,
  getShortPosition,
  getRivalTeamIdByMatchId,
  isThick,
  getRivalIds,
  getEmojiByPlayerForm,
} from "../../helpers/player";
import { SettingsContext } from "../../providers/SettingsProvider";
import styles from "./index.module.scss";
import API from "../../api";
import { useQuery } from "react-query";
import { getRuleText } from "../../helpers/squad";
import Message from "../../components/Message";
import { getTeamLogoUrl, rndInt } from "../../helpers/utils";
import { isPlayerTeamPlayed } from "../../helpers/player";
import CustomAnimatedNumber from "../../components/CustomAnimatedNumber";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import ProgressLine from "../../components/ProgressLine";
import Shirt from "../../components/Shirt";
import PlayerImage from "../../components/PlayerImage";

const responsive = {
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 6,
    slidesToSlide: 5, // optional, default to 1.
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 4,
    slidesToSlide: 3, // optional, default to 1.
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 3,
    slidesToSlide: 2, // optional, default to 1.
  },
};

const getPlayerSumPts = (playerStats = []) => {
  return playerStats.reduce((acc, value) => acc + value.Pts, 0);
};

const getRoundPts = (playerStats = [], round) => {
  return playerStats.find((stat) => stat.Round === round)?.Pts || "0";
};

const getSelectedRoundInfo = (playerStats = [], round) => {
  return playerStats.find((stat) => stat.Round === round);
};

const isSelectedRound = (selectedRound, round) => {
  return round === selectedRound;
};

const PlayerInfo = ({ show, onHide, player }) => {
  const carouselRef = useRef(null);
  const detailsRef = useRef(null);
  const [playerStats, setPlayerStats] = useState(null);
  const [sumPts, setSumPts] = useState(0);
  const [allRoundsList, setAllRoundsList] = useState([]);
  const [selectedRoundInfo, setSelectedRoundInfo] = useState(null);
  const [selectedRound, setSelectedRound] = useState(null);
  const [carouselVisible, setCarouselVisible] = useState(false);
  const [progressLineVisible, setProgressLineVisible] = useState(true);

  const {
    currentRound,
    activeDeadline,
    lastRound,
    teams,
    roundMatches,
  } = useContext(SettingsContext);

  const isFirstRound = currentRound === 1;

  useEffect(() => {
    setAllRoundsList(
      Array.from(Array(lastRound).keys())
        .map((Item) => Item + 1)
        .sort((a, b) => a - b)
        .filter((Item) => Item <= lastRound)
    );
  }, [currentRound, lastRound]);

  const isCalculated = isPlayerTeamPlayed(player, roundMatches);

  useQuery(
    "query-player-stats",
    async () => {
      return await API.fetchPlayerStats(
        player.PlayerId,
        undefined,
        currentRound,
        player?.teamId
      );
    },
    {
      enabled: show === true,
      staleTime: 1,
      onSuccess: (res) => {
        setPlayerStats(
          res.data.map((Item) => ({
            ...Item,
            Details: Item.Details ? JSON.parse(Item.Details) : [],
          }))
        );
      },
      onError: (err) => {
        console.log(err.response?.data || err);
      },
    }
  );

  useEffect(() => {
    if (!playerStats?.length || currentRound === null) return;
    const newSumPts = getPlayerSumPts(playerStats);
    const newSelectedRoundInfo = getSelectedRoundInfo(
      playerStats,
      currentRound
    );
    setSumPts(newSumPts);
    setSelectedRoundInfo(newSelectedRoundInfo);
    setSelectedRound(currentRound);
  }, [playerStats, currentRound]);

  const onClickRoundItem = (round) => {
    setSelectedRoundInfo(getSelectedRoundInfo(playerStats, round));
    setSelectedRound(round);
    if (detailsRef.current)
      setTimeout(
        () =>
          detailsRef.current.scrollIntoView({
            behavior: "smooth",
            block: "end",
          }),
        0
      );
  };

  const hideModal = () => {
    onHide();

    setTimeout(() => {
      setSelectedRoundInfo(null);
      setSelectedRound(null);
      setSumPts(0);
      setPlayerStats(null);
      setCarouselVisible(false);
      setProgressLineVisible(true);
    }, 200);
  };

  const isDataAvailable = playerStats !== null;

  useEffect(() => {
    if (!carouselRef.current || !isDataAvailable) return;

    const targetSlide =
      currentRound <= lastRound - carouselRef.current.state.slidesToShow
        ? currentRound < 2
          ? 0
          : currentRound - 2
        : currentRound - carouselRef.current.state.slidesToShow - 1;

    carouselRef.current.goToSlide(targetSlide);
    setCarouselVisible(true);
    setTimeout(() => {
      setProgressLineVisible(false);
    }, 500);
  }, [isDataAvailable, lastRound, currentRound]);

  const getScore = (firstScore, secondScore) => {
    firstScore = firstScore !== undefined ? firstScore : "-";
    secondScore = secondScore !== undefined ? secondScore : "-";
    return `${firstScore}:${secondScore}`;
  };

  return (
    <Modal
      show={show}
      size="lg"
      onHide={hideModal}
      backdrop={true}
      keyboard={true}
    >
      <Modal.Header closeButton style={{ position: "relative" }}>
        <Modal.Title>
          <div className={styles.photoCnt}>
            {process.env.REACT_APP_PHOTO_ENABLED ? (
              <PlayerImage player={player} size="lg" lazy={false} />
            ) : (
              <Shirt
                age={player?.age}
                teamId={player?.teamId}
                number={player?.number}
                showAge={false}
              />
            )}
            <div className={styles.info}>
              <div className={styles.name}>
                <div className={styles.wrapper}>
                  <div>
                    <div>{player?.FirstName}</div>
                    <div>{player?.LastName}</div>
                  </div>
                  <div className={styles.hint}>
                    {getPlayerShortTeam(teams, player)} |{" "}
                    {getShortPosition(player?.position)}{" "}
                    {!process.env.REACT_APP_PHOTO_ENABLED && (
                      <>| ВІК {player?.age}</>
                    )}
                  </div>
                </div>
                {process.env.REACT_APP_PHOTO_ENABLED && (
                  <Shirt
                    age={player?.age}
                    teamId={player?.teamId}
                    number={player?.number}
                  />
                )}
              </div>
            </div>
          </div>
          <div className={styles.panel}>
            <div className={styles.col}>
              <span className={styles.caption}>За сезон</span>
              <Badge
                className={`${styles.darkTextBadge} ${styles.badge}`}
                bg={"primary"}
              >
                <CustomAnimatedNumber value={sumPts} />
              </Badge>
            </div>
            <div className={styles.col}>
              <span className={styles.caption}>Ціна</span>
              <Badge
                className={`${styles.lightTextBadge} ${styles.badge}`}
                bg={"success"}
              >
                {player?.Cost?.toFixed(1)}&nbsp;
                <i className="bi bi-coin" />
              </Badge>
            </div>
            {!isFirstRound && activeDeadline && (
              <div className={styles.col}>
                <span className={styles.caption}>Форма</span>
                <Badge
                  className={`${styles.darkTextBadge} ${styles.badge}`}
                  bg={"warning"}
                >
                  {player?.Form?.toFixed(1)}
                  &nbsp;
                  <i className={`bi ${getEmojiByPlayerForm(player?.Form)}`} />
                </Badge>
              </div>
            )}
          </div>
        </Modal.Title>
        {progressLineVisible && <ProgressLine />}
      </Modal.Header>
      <Modal.Body
        className={styles.modalBody}
        style={{ minHeight: "70vh", opacity: carouselVisible ? 1 : 0 }}
      >
        {isDataAvailable ? (
          <Carousel
            ref={carouselRef}
            containerClass={styles.listContainer}
            responsive={responsive}
            slidesToSlide={3}
          >
            {playerStats.map(({ Round, matches }) => (
              <div
                key={Round}
                className={`${styles.listItem} ${
                  isSelectedRound(selectedRound, Round) ? styles.selected : ""
                }`}
                onClick={() => onClickRoundItem(Round)}
              >
                <div className={styles.rivalCnt}>
                  <Badge className={styles.matchBadge} bg="white">
                    {getRivalIds(playerStats, Round)?.length ||
                    currentRound < Round ||
                    (!isCalculated && Round === currentRound) ? (
                      matches.map(
                        ({
                          MatchId,
                          FirstTeam,
                          FirstScore,
                          SecondScore,
                          SecondTeam,
                        }) => (
                          <div key={MatchId} className={styles.match}>
                            <img
                              className={styles.rivalLogo}
                              src={getTeamLogoUrl(FirstTeam, rndInt())}
                              alt=""
                              style={{ marginRight: 2 }}
                            />
                            {teams[FirstTeam]?.shortName}{" "}
                            {getScore(FirstScore, SecondScore)}{" "}
                            {teams[SecondTeam]?.shortName}
                            <img
                              className={styles.rivalLogo}
                              src={getTeamLogoUrl(SecondTeam, rndInt())}
                              alt=""
                              style={{ marginLeft: 2 }}
                            />
                            <br />{" "}
                          </div>
                        )
                      )
                    ) : (
                      <div className={styles.didntPlay}>НЕ ГРАВ</div>
                    )}
                  </Badge>
                </div>
                <div className={styles.bottomLine}>
                  <div className={styles.round}>
                    <Badge className={styles.ptsBadge} bg="warning">
                      ТУР {Round}
                    </Badge>
                  </div>

                  <div className={styles.pts}>
                    <Badge className={styles.ptsBadge} bg="primary">
                      {!isCalculated && Round === currentRound ? (
                        <i
                          className="bi bi-hourglass darkText"
                          style={{ fontSize: "0.8rem" }}
                        ></i>
                      ) : (
                        <>{getRoundPts(playerStats, Round)}</>
                      )}
                    </Badge>
                  </div>
                </div>
              </div>
            ))}
          </Carousel>
        ) : (
          <Carousel
            containerClass={styles.listContainer}
            responsive={responsive}
            slidesToSlide={3}
          >
            {allRoundsList.map((round) => (
              <div
                key={round}
                className={styles.listItem}
                style={{ padding: 0, border: 0 }}
              >
                <Placeholder
                  xs={2}
                  style={{
                    width: "100%",
                    height: "100%",
                  }}
                />
              </div>
            ))}
          </Carousel>
        )}
        <div ref={detailsRef}>
          {selectedRoundInfo?.Details?.length > 0 ? (
            <div className={styles.detailsContainer}>
              <div className={styles.detailsItem}>
                <div className={styles.key} style={{ fontWeight: "bold" }}>
                  Правила
                </div>
                <div className={styles.value}>Бали</div>
              </div>
              {selectedRoundInfo?.Details?.map((item, index, items) => (
                <div
                  key={index}
                  className={`${styles.detailsItem} ${
                    isThick(item.id, items, index) ? styles.thick : ""
                  }`}
                >
                  <div className={styles.key}>
                    <img
                      className={styles.rivalLogo}
                      src={getTeamLogoUrl(
                        getRivalTeamIdByMatchId(
                          item.id,
                          items,
                          getRivalIds(playerStats, selectedRound),
                          index
                        ),
                        rndInt()
                      )}
                      alt=""
                    />
                    <span style={{ marginLeft: 8 }}>
                      {getRuleText(item.key)}
                    </span>
                  </div>
                  <div className={styles.value}> {item.value} </div>
                </div>
              ))}
            </div>
          ) : (
            isDataAvailable && (
              <div className={styles.msgCnt}>
                <Message text="Немає даних" />{" "}
              </div>
            )
          )}{" "}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default PlayerInfo;
